/**
 * What fills the shell: either a screener, or a chart.
 *
 * Never both. The screener used to render above the chart, so opening a
 * criterion gave you a screener header sitting on top of the full dashboard
 * for whatever instrument was loaded — and a failed screener showed its error
 * with a working chart underneath, which reads as an answer. A screener is a
 * place you go, not a banner you get.
 *
 * Split out of App so the mode is a pure function of props and can be
 * rendered in every state without a DOM, a fetch or a router.
 */
import type { ReactNode } from "react";
import type { EChartsOption } from "echarts";

import type { ChartResult } from "../api/client.ts";
import { EChart } from "../chart/EChart.tsx";
import { PROFILE_NAME } from "../chart/option.ts";
import { EvidencePanel } from "./EvidencePanel.tsx";
import { ScreenerResultsView, type ScreenerState } from "./ScreenerResultsView.tsx";
import { SummaryHeader } from "./SummaryHeader.tsx";
import { displayNameFor } from "../domain/instruments.ts";
import type { AnalysisMethod } from "../api/client.ts";

export type ChartState =
	| { status: "loading" }
	| { status: "ready"; result: ChartResult }
	| { status: "error"; message: string };

interface WorkspaceProps {
	code: string;
	method: AnalysisMethod;
	chartState: ChartState;
	option: EChartsOption | null;
	/** The open criterion's label and hint, or null for the chart workspace. */
	screener: { label: string; hint: string } | null;
	/** Null until the fetch effect has run. An open criterion with no state
	 *  yet is loading, not "no screener" — treating it as the latter would
	 *  flash the chart for one paint on the way in. */
	screenerState: ScreenerState | null;
	onSelectInstrument: (code: string) => void;
	onCloseScreener: () => void;
	onRetryScreener: () => void;
	/** The Foreign/Whale control. Both modes need it, so both are handed it. */
	flowToggle: ReactNode;
	/** Named in the chart error so an offline build says where it was looking. */
	apiBase: string | null;
	/** Details panel collapsed to a strip. No panel exists in screener mode. */
	detailsCollapsed?: boolean;
	onToggleDetails?: () => void;
}

export function Workspace({
	code,
	method,
	chartState,
	option,
	screener,
	screenerState,
	onSelectInstrument,
	onCloseScreener,
	onRetryScreener,
	flowToggle,
	apiBase,
	detailsCollapsed = false,
	onToggleDetails,
}: WorkspaceProps) {
	// One condition, and it is the same one the shell sets data-mode from:
	// an open criterion means this is a screener, full stop.
	if (screener) {
		return (
			<main className="workspace">
				<ScreenerResultsView
					label={screener.label}
					hint={screener.hint}
					method={method}
					state={screenerState ?? { status: "loading" }}
					flowToggle={flowToggle}
					onSelectInstrument={onSelectInstrument}
					onRetry={onRetryScreener}
					onClose={onCloseScreener}
				/>
			</main>
		);
	}

	const chart = chartState.status === "ready" ? chartState.result.chart : null;

	return (
		<>
			<main className="workspace">
				{chartState.status === "loading" ? <p className="notice">Loading {displayNameFor(code)}…</p> : null}

				{chartState.status === "error" ? (
					<div className="notice notice--error" role="alert">
						<p>{chartState.message}</p>
						<p className="notice__hint">
							This build reads charts from <code>{apiBase ?? "the bundled COMPOSITE fixture"}</code>. Set{" "}
							<code>VITE_QUANTIST_API_BASE</code> to the Cloudflare worker to load other instruments.
						</p>
					</div>
				) : null}

				{chart && option ? (
					<>
						<SummaryHeader chart={chart} flowToggle={flowToggle} />
						<EChart
							className="workspace__chart"
							option={option}
							ariaLabel={`${chart.instrument.display_name} ${chart.annotations.method_label} chart: candlesticks, ${chart.annotations.abbrev}-VWAP, value flow, proportion, net value, and the ${PROFILE_NAME} as a ${chart.profile_zones.length}-bar histogram in a strip beside the price. The Details panel lists the same values as text.`}
						/>
					</>
				) : null}
			</main>

			{/* A sibling, not a child: `.shell > .evidence` is a direct-child rule. */}
			{chart ? (
				<EvidencePanel chart={chart} collapsed={detailsCollapsed} onToggleCollapsed={onToggleDetails} />
			) : null}
		</>
	);
}
